import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Grid, Box, Typography, Paper, makeStyles } from '@material-ui/core';
import Done from '@material-ui/icons/Done';
import { Link } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import { Input, Select, Checkbox } from 'components/FormInputs';
import { useFormik } from 'components/shared/Formik';
import ImageGallery from 'components/shared/ImageGallery';
import { validate } from './complaintFormValidate';
import { MAX_DESCRIPTION_LENGTH } from '../constants';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILE_COUNT = 10;

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
}));

const ComplaintForm = ({
  categories,
  isUpdating,
  conflict,
  initialValues,
  onSubmit,
  booking,
  editMode,
  existingImages,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [stagedFiles, setStagedFiles] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [fileError, setFileError] = useState(null);
  const fileInputRef = useRef(null);
  const stagedFilesRef = useRef(stagedFiles);
  stagedFilesRef.current = stagedFiles;

  useEffect(() => {
    return () => stagedFilesRef.current.forEach((sf) => URL.revokeObjectURL(sf.previewUrl));
  }, []);

  const formik = useFormik({
    validate: validate({ intl, editMode, maxCompensation: booking?.total_price }),
    initialValues,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) =>
      onSubmit(
        values,
        stagedFiles.map((sf) => sf.file),
        removedImageIds,
      ),
  });

  const handleFileSelect = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      setFileError(null);
      const tooLarge = files.filter((f) => f.size > MAX_FILE_SIZE);
      if (tooLarge.length) {
        setFileError(
          intl.formatMessage(
            { id: 'complaints.fileTooLarge', defaultMessage: '{name} exceeds {max}MB' },
            { name: tooLarge.map((f) => f.name).join(', '), max: MAX_FILE_SIZE / (1024 * 1024) },
          ),
        );
        e.target.value = '';
        return;
      }
      setStagedFiles((prev) => {
        const existingCount = existingImages ? existingImages.length - removedImageIds.length : 0;
        const remaining = MAX_FILE_COUNT - existingCount - prev.length;
        if (remaining <= 0 || files.length > remaining) {
          setFileError(
            intl.formatMessage(
              { id: 'complaints.tooManyFiles', defaultMessage: 'Maximum {max} images' },
              { max: MAX_FILE_COUNT },
            ),
          );
          if (remaining <= 0) return prev;
          const accepted = files.slice(0, remaining);
          return [
            ...prev,
            ...accepted.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) })),
          ];
        }
        return [...prev, ...files.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }))];
      });
      e.target.value = '';
    },
    [intl, existingImages, removedImageIds],
  );

  const removeFile = useCallback((index) => {
    setStagedFiles((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const {
    values,
    errors,
    setFieldValue,
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    isSubmitting,
  } = formik;

  const isDisabled = isUpdating || isSubmitting || !isValid || conflict;

  const categoryOptions = categories.map((cat) => ({
    label: intl.formatMessage({
      id: `complaints.category.${cat.key}`,
      defaultMessage: cat.name,
    }),
    value: String(cat.id),
  }));

  return (
    <Paper className={classes.paper}>
      {booking && (
        <Box mb={2}>
          <Typography variant="subtitle2" color="textSecondary">
            <FormattedMessage id="complaints.booking" defaultMessage="Booking" />
            {': '}#{booking.id}
            {booking.cottage_name && ` - ${booking.cottage_name}`}
          </Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Select
              name="category_id"
              label={intl.formatMessage({ id: 'complaints.category', defaultMessage: 'Category' })}
              value={values.category_id}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              options={categoryOptions}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Input
              name="description"
              label={intl.formatMessage({
                id: 'complaints.description',
                defaultMessage: 'Description',
              })}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              multiline
              minRows={5}
              maxRows={15}
              variant="outlined"
              required
            />
            <Box textAlign="right">
              <Typography
                variant="body2"
                color={values.description?.length > MAX_DESCRIPTION_LENGTH ? 'error' : 'initial'}
              >
                {values.description?.length || 0}/{MAX_DESCRIPTION_LENGTH}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Input
              name="desired_compensation"
              label={intl.formatMessage({
                id: 'complaints.desiredCompensation',
                defaultMessage: 'Desired compensation (SEK)',
              })}
              type="number"
              value={values.desired_compensation}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              <FormattedMessage id="complaints.images" defaultMessage="Images" />
            </Typography>

            <ImageGallery
              images={[
                ...(editMode && existingImages
                  ? existingImages.filter((img) => !removedImageIds.includes(img.id))
                  : []),
                ...stagedFiles.map((sf) => ({
                  id: sf.previewUrl,
                  url: sf.previewUrl,
                })),
              ]}
              onDelete={(idx) => {
                const visibleExisting =
                  editMode && existingImages
                    ? existingImages.filter((img) => !removedImageIds.includes(img.id))
                    : [];
                if (idx < visibleExisting.length) {
                  setRemovedImageIds((prev) => [...prev, visibleExisting[idx].id]);
                } else {
                  removeFile(idx - visibleExisting.length);
                }
              }}
              onAdd={() => fileInputRef.current.click()}
              maxCount={MAX_FILE_COUNT}
            />

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".jpeg,.jpg,.png"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            {fileError && (
              <Typography variant="body2" color="error" style={{ marginTop: 4 }}>
                {fileError}
              </Typography>
            )}
          </Grid>

          {!editMode && (
            <Grid item xs={12}>
              <Checkbox
                name="host_informed"
                value={values.host_informed}
                onChange={(e) => setFieldValue('host_informed', e.target.checked)}
                errors={errors}
                label={
                  <FormattedMessage
                    id="complaints.hostInformed"
                    defaultMessage="I confirm that I have informed the host about this issue"
                  />
                }
              />
              {booking?.conversation_id && (
                <Box mt={1}>
                  <Alert severity="info">
                    <FormattedMessage
                      id="complaints.contactHostFirst"
                      defaultMessage="Only the host can help you during your stay. {link}"
                      values={{
                        link: (
                          <Link to={`/conversations/${booking.conversation_id}`}>
                            <FormattedMessage
                              id="complaints.goToConversation"
                              defaultMessage="Go to conversation"
                            />
                          </Link>
                        ),
                      }}
                    />
                  </Alert>
                </Box>
              )}
            </Grid>
          )}
        </Grid>

        <Button
          color="primary"
          type="submit"
          variant="contained"
          disabled={isDisabled}
          className={classes.button}
        >
          <Done />
          {editMode ? (
            <FormattedMessage id="complaints.save" defaultMessage="Save changes" />
          ) : (
            <FormattedMessage id="complaints.submit" defaultMessage="Submit complaint" />
          )}
        </Button>
      </form>
    </Paper>
  );
};

ComplaintForm.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      suggested_compensation: PropTypes.shape({
        min: PropTypes.number,
        max: PropTypes.number,
      }),
    }),
  ),
  isUpdating: PropTypes.bool,
  conflict: PropTypes.bool,
  initialValues: PropTypes.shape({}),
  onSubmit: PropTypes.func.isRequired,
  booking: PropTypes.shape({
    id: PropTypes.number,
    cottage_name: PropTypes.string,
    conversation_id: PropTypes.number,
    total_price: PropTypes.number,
  }),
  editMode: PropTypes.bool,
  existingImages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      url: PropTypes.string,
    }),
  ),
};

export default ComplaintForm;
